import { Component, Input, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  Validators
} from '@angular/forms';
import { MeasurementUnit, Unit } from '@ml/services/types/service';
import { positiveNumberValidator } from '@ml/shared/validators';

@Component({
  selector: 'ml-measurement-unit-form',
  template: `
    <div fxLayout="column" fxLayoutGap="12px">
      <div
        [formGroup]="form"
        *ngFor="
          let form of form.controls;
          let i = index;
          let isLast = last;
          let isFirst = first
        "
      >
        <div fxLayout="row wrap" fxLayoutAlign="space-between">
          <mat-form-field
            class="unit"
            [fxFlex]="canManage || canAddWork || canAddQuantity ? 15 : 20"
          >
            <mat-label>{{ 'unit' | translate }}</mat-label>
            <mat-select formControlName="unit">
              <mat-option *ngFor="let unit of unitValues()" [value]="unit">
                {{ unit }}
              </mat-option>
            </mat-select>
            <mat-error *ngIf="unitControl(form).hasError('required')">
              {{ 'unit' | translate }} {{ 'is' | translate }}
              <strong>{{ 'required' | translate }}</strong>
            </mat-error>
          </mat-form-field>
          <mat-form-field
            class="price"
            [fxFlex]="canEstimate || canAddWork || canAddQuantity ? 18 : 25"
          >
            <mat-label>{{ 'base' | translate }}</mat-label>
            <input type="number" matInput formControlName="basePrice" />
            <span matSuffix>€</span>
            <mat-error *ngIf="basePriceControl(form).hasError('required')">
              {{ 'base' | translate }} {{ 'is' | translate }}
              <strong>{{ 'required' | translate }}</strong>
            </mat-error>
            <mat-error
              *ngIf="
                !basePriceControl(form).hasError('required') &&
                basePriceControl(form).hasError('pattern')
              "
            >
              {{ 'base-must-be-number' | translate }}
            </mat-error>
            <mat-error
              *ngIf="basePriceControl(form).hasError('validatePositiveNumber')"
            >
              {{ 'positive number' | translate }}
            </mat-error>
          </mat-form-field>
          <mat-form-field
            class="price"
            [fxFlex]="canEstimate || canAddWork || canAddQuantity ? 18 : 25"
          >
            <mat-label>{{ 'net' | translate }}</mat-label>
            <input matInput type="number" formControlName="netPrice" />
            <span matSuffix>€</span>
            <mat-error *ngIf="netPriceControl(form).hasError('required')">
              {{ 'net' | translate }} {{ 'is' | translate }}
              <strong>{{ 'required' | translate }}</strong>
            </mat-error>
            <mat-error
              *ngIf="
                !netPriceControl(form).hasError('required') &&
                netPriceControl(form).hasError('pattern')
              "
            >
              {{ 'net-must-be-number' | translate }}
            </mat-error>
            <mat-error
              *ngIf="netPriceControl(form).hasError('validatePositiveNumber')"
            >
              {{ 'positive number' | translate }}
            </mat-error>
          </mat-form-field>
          <mat-form-field
            class="price"
            [fxFlex]="canEstimate || canAddWork || canAddQuantity ? 18 : 25"
          >
            <mat-label>{{ 'tax' | translate }}</mat-label>
            <input type="number" matInput formControlName="taxPrice" />
            <span matSuffix>%</span>
            <mat-error *ngIf="taxPriceControl(form).hasError('required')">
              {{ 'tax' | translate }} {{ 'is' | translate }}
              <strong>{{ 'required' | translate }}</strong>
            </mat-error>
            <mat-error
              *ngIf="
                !taxPriceControl(form).hasError('required') &&
                taxPriceControl(form).hasError('pattern')
              "
            >
              {{ 'tax-must-be-number' | translate }}
            </mat-error>
            <mat-error
              *ngIf="taxPriceControl(form).hasError('validatePositiveNumber')"
            >
              {{ 'positive number' | translate }}
            </mat-error>
          </mat-form-field>

          <ml-input
            fxFlex="20"
            *ngIf="canEstimate"
            [control]="estimatedQuantity(form)"
            [label]="'estimation-quantity'"
            [type]="'number'"
            [suffixText]="unitControl(form).value"
          ></ml-input>

          <ml-input
            fxFlex="21"
            *ngIf="canAddWork"
            [control]="workedQuantity(form)"
            [label]="'quantity'"
            [type]="'number'"
            [suffixText]="unitControl(form).value"
          ></ml-input>
          <ml-input
            fxFlex="21"
            *ngIf="canAddQuantity"
            [control]="actualQuantity(form)"
            [label]="'quantity'"
            [type]="'number'"
            [suffixText]="unitControl(form).value"
          ></ml-input>

          <div fxLayout="column" *ngIf="canManage" fxFlex="5">
            <button
              type="button"
              mat-icon-button
              (click)="onRemove(i)"
              *ngIf="!isFirst || !isLast"
              class="action-button"
            >
              <mat-icon svgIcon="delete"></mat-icon>
            </button>

            <button
              type="button"
              mat-icon-button
              color="primary"
              class="action-button"
              [disabled]="form.invalid"
              (click)="onAdd()"
              *ngIf="isLast"
            >
              <mat-icon svgIcon="add"></mat-icon>
            </button>
          </div>
        </div>
      </div>
    </div>
  `,
  styleUrls: ['./measurement-unit-form.component.scss']
})
export class MeasurementUnitFormComponent implements OnInit {
  @Input() form: FormArray;
  @Input() initialData: MeasurementUnit[];
  @Input() withInitialData = true;
  @Input() canManage = true;
  @Input() canEstimate = false;
  @Input() canAddWork = false;
  @Input() canAddQuantity = false;
  @Input() positiveBasePrice = true;
  @Input() positiveNetPrice = true;

  constructor(private formBuilder: FormBuilder) {}

  ngOnInit() {
    if (!this.initialData) {
      this.onAdd();
    } else {
      for (const unit of this.initialData) {
        this.onAdd(unit);
      }
    }
  }

  onAdd(unit?: MeasurementUnit) {
    const basePriceValidators = [Validators.required];
    const netPriceValidators = [Validators.required];

    if (this.positiveBasePrice) {
      basePriceValidators.push(positiveNumberValidator);
    }
    if (this.positiveNetPrice) {
      netPriceValidators.push(positiveNumberValidator);
    }

    const form = this.formBuilder.group({
      unit: [unit ? unit.unit : '', Validators.required],
      basePrice: [unit ? unit.basePrice : '', basePriceValidators],
      netPrice: [unit ? unit.netPrice : '', netPriceValidators],
      taxPrice: [
        unit ? unit.taxPrice : '',
        [Validators.required, positiveNumberValidator]
      ]
    });
    if (this.canEstimate || (unit && unit.estimatedQuantity)) {
      form.addControl(
        'estimatedQuantity',
        new FormControl(unit ? unit.estimatedQuantity : null, [
          positiveNumberValidator
        ])
      );
    }
    if (this.canAddWork || (unit && unit.workedQuantity)) {
      form.addControl(
        'workedQuantity',
        new FormControl(unit ? unit.workedQuantity : null, [
          Validators.required,
          positiveNumberValidator
        ])
      );
    }
    if (this.canAddQuantity) {
      form.addControl(
        'actualQuantity',
        new FormControl(unit ? unit.actualQuantity : null, [
          Validators.required,
          positiveNumberValidator
        ])
      );
    }
    this.form.push(form);
  }

  setMeasurementUnits(measurementUnits: MeasurementUnit[]) {
    for (const unit of measurementUnits) {
      this.onAdd(unit);
    }
  }

  clearForm() {
    const length = this.form.length;
    for (let i = 0; i < length; i++) {
      this.form.removeAt(0);
    }
  }

  getMeasurementUnits(setWorkedQuantity = true): MeasurementUnit[] {
    const measurements: MeasurementUnit[] = [];
    for (const unit of this.form.value) {
      measurements.push(new MeasurementUnit(unit, 1));
    }

    return measurements;
  }

  onRemove(index: number) {
    this.form.removeAt(index);
  }

  unitValues() {
    return Object.keys(Unit);
  }

  unitControl(form: AbstractControl) {
    return form.get('unit');
  }

  basePriceControl(form: AbstractControl) {
    return form.get('basePrice');
  }

  netPriceControl(form: AbstractControl) {
    return form.get('netPrice');
  }

  taxPriceControl(form: AbstractControl) {
    return form.get('taxPrice');
  }

  estimatedQuantity(form: AbstractControl) {
    return form.get('estimatedQuantity') as FormControl;
  }

  workedQuantity(form: AbstractControl) {
    return form.get('workedQuantity') as FormControl;
  }

  actualQuantity(form: AbstractControl) {
    return form.get('actualQuantity') as FormControl;
  }
}
